import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme, TOKENS } from '../context/ThemeContext';
import { FORMULA_CHALLENGES } from '../data/formulaChallenges';

export default function FormulaChallengeList() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const c = TOKENS[theme];

  const [filter, setFilter] = useState('all'); // all | formula | validation
  const [difficulty, setDifficulty] = useState('all');

  const filtered = FORMULA_CHALLENGES.filter(ch => {
    const typeMatch = filter === 'all' || ch.type === filter;
    const diffMatch = difficulty === 'all' || ch.difficulty === difficulty;
    return typeMatch && diffMatch;
  });

  const formulaCount = FORMULA_CHALLENGES.filter(c => c.type === 'formula').length;
  const validationCount = FORMULA_CHALLENGES.filter(c => c.type === 'validation').length;

  const diffStyle = {
    Easy:   { color: c.success, bg: theme === 'light' ? '#d1fae5' : '#051a10' },
    Medium: { color: c.warning, bg: theme === 'light' ? '#fef3c7' : '#1a1000' },
    Hard:   { color: c.error,   bg: theme === 'light' ? '#fee2e2' : '#1a0808' },
  };

  const typeColor = (type) => type === 'validation' ? '#8b5cf6' : c.accent;
  const typeBg = (type) => type === 'validation'
    ? (theme === 'light' ? '#ede9fe' : '#1a0a2e')
    : c.accentBg;

  return (
    <div style={{ background: c.bg, minHeight: '100vh', color: c.text, fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* Navbar */}
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 clamp(24px, 4vw, 80px)', height: 58, background: c.navBg, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${c.border}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <span style={{ fontSize: 20, fontWeight: 800, cursor: 'pointer', letterSpacing: '-0.5px' }} onClick={() => navigate('/')}>
          Trail<span style={{ color: c.accent }}>Code</span>
        </span>
        <div style={{ display: 'flex', gap: 6, fontSize: 13, color: c.muted }}>
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>Home</span>
          <span>›</span>
          <span style={{ color: c.secondary }}>Formula Challenges</span>
        </div>
        <button onClick={() => navigate(-1)} style={{ background: 'transparent', border: `1px solid ${c.border}`, color: c.secondary, padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>← Back</button>
      </nav>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '40px clamp(24px, 4vw, 60px) 80px' }}>

        {/* Header */}
        <div style={{ marginBottom: 36 }}>
          <p style={{ color: c.accent, fontSize: 11, letterSpacing: '2px', textTransform: 'uppercase', marginBottom: 10, fontWeight: 600 }}>Formula & Validation</p>
          <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-1px', marginBottom: 12 }}>Formula Challenges</h1>
          <p style={{ fontSize: 14, color: c.secondary, lineHeight: 1.7, maxWidth: 560, marginBottom: 24 }}>
            Build real Salesforce formula fields and validation rules — evaluated instantly against test records, no org required. The same skills used daily by admins and developers.
          </p>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>⚡</span>
              <div>
                <p style={{ fontSize: 18, fontWeight: 700, color: c.accent, margin: 0 }}>{formulaCount}</p>
                <p style={{ fontSize: 11, color: c.secondary, margin: 0 }}>Formula Fields</p>
              </div>
            </div>
            <div style={{ background: theme === 'light' ? '#ede9fe' : '#1a0a2e', border: '1px solid #8b5cf630', borderRadius: 8, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>🛡</span>
              <div>
                <p style={{ fontSize: 18, fontWeight: 700, color: '#8b5cf6', margin: 0 }}>{validationCount}</p>
                <p style={{ fontSize: 11, color: '#8b5cf6', margin: 0, opacity: 0.7 }}>Validation Rules</p>
              </div>
            </div>
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 24 }}>🚀</span>
              <div>
                <p style={{ fontSize: 18, fontWeight: 700, color: c.text, margin: 0 }}>No org needed</p>
                <p style={{ fontSize: 11, color: c.secondary, margin: 0 }}>Client-side evaluation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {[['all', 'All'], ['formula', '⚡ Formula Fields'], ['validation', '🛡 Validation Rules']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)} style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${filter === val ? c.accent : c.border}`, background: filter === val ? c.accentBg : 'transparent', color: filter === val ? c.accent : c.secondary, cursor: 'pointer', fontSize: 13, fontWeight: filter === val ? 600 : 400, transition: 'all 0.15s' }}>
              {label}
            </button>
          ))}
          <div style={{ width: 1, background: c.border, margin: '0 4px' }} />
          {['all', 'Easy', 'Medium', 'Hard'].map(d => (
            <button key={d} onClick={() => setDifficulty(d)} style={{ padding: '8px 14px', borderRadius: 6, border: `1px solid ${difficulty === d ? c.accent : c.border}`, background: difficulty === d ? c.accentBg : 'transparent', color: difficulty === d ? c.accent : c.secondary, cursor: 'pointer', fontSize: 12, fontWeight: difficulty === d ? 600 : 400, transition: 'all 0.15s' }}>
              {d === 'all' ? 'All Levels' : d}
            </button>
          ))}
        </div>

        {/* Challenge list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((ch, i) => {
            const ds = diffStyle[ch.difficulty] || diffStyle.Easy;
            return (
              <div key={ch.id}
                onClick={() => navigate(`/challenges/formula/${ch.id}`)}
                style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 10, padding: '18px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 16, transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = typeColor(ch.type); e.currentTarget.style.transform = 'translateX(4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = c.border; e.currentTarget.style.transform = 'translateX(0)'; }}>

                {/* Number */}
                <div style={{ minWidth: 32, height: 32, borderRadius: '50%', background: c.bg, border: `1.5px solid ${c.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: c.secondary, flexShrink: 0 }}>
                  {String(i + 1).padStart(2, '0')}
                </div>

                {/* Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <p style={{ fontSize: 14, fontWeight: 600, margin: 0, color: c.text }}>{ch.title}</p>
                  </div>
                  <p style={{ fontSize: 12, color: c.secondary, margin: 0 }}>{ch.topic}</p>
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: typeColor(ch.type), background: typeBg(ch.type), padding: '2px 8px', borderRadius: 4 }}>
                    {ch.type === 'validation' ? '🛡 Validation' : '⚡ Formula'}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: ds.color, background: ds.bg, padding: '2px 8px', borderRadius: 4 }}>
                    {ch.difficulty}
                  </span>
                  <span style={{ fontSize: 11, color: c.muted }}>{ch.testCases.length} tests</span>
                  <span style={{ fontSize: 18, color: c.muted }}>→</span>
                </div>
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: c.muted }}>
              <p style={{ fontSize: 32, marginBottom: 12 }}>🔍</p>
              <p>No challenges match your filters</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
